<template>
  <div class="seo-page">
    <section class="seo-hero">
      <span class="seo-badge">Steam Total Hours</span>
      <h1>See Your Total Steam Hours Across Every Game</h1>
      <p>
        Steam tracks hours on record for each game, but never adds them together — there is no account-wide total
        anywhere in the client or on your profile. steamplayti.me sums your whole library into one all-time number,
        keeps it on your dashboard, and shows which games it came from.
      </p>
      <div class="seo-cta-group">
        <router-link class="seo-primary-btn" to="/">Get my total Steam hours</router-link>
        <a class="seo-secondary-link" href="#how-total-works">How the total is counted →</a>
      </div>
      <ul class="seo-highlight-list">
        <li>
          <span class="seo-highlight-icon">➕</span>
          <div>One total for your whole account, summed from every game's hours on record.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">🏆</span>
          <div>Your most-played games ranked, so you can see where the hours actually went.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">📈</span>
          <div>From then on, a daily record of how that total grows.</div>
        </li>
      </ul>
    </section>

    <section class="seo-section" id="how-total-works">
      <h2>How Your Total Is Counted</h2>
      <p>
        The total comes straight from Steam: the same lifetime hours on record your profile shows per game, added
        up across every game on your account. Nothing is estimated, and nothing needs to be entered by hand.
      </p>
      <div class="seo-grid">
        <div class="seo-card">
          <h3>Whole Library</h3>
          <p>Every game on your account counts — including ones you have not touched in years, as long as the playtime is on your account.</p>
        </div>
        <div class="seo-card">
          <h3>Top Games</h3>
          <p>Your library is ranked by playtime alongside the total, so the biggest share of your hours is right at the top.</p>
        </div>
        <div class="seo-card">
          <h3>Achievements Too</h3>
          <p>Next to your hours you also get how many achievements you have unlocked across the library.</p>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>A Total That Keeps Counting</h2>
      <p>
        A lifetime total tells you where you are; it cannot tell you how you got there. Once your profile is tracked,
        steamplayti.me saves a snapshot every day, so you can see how many hours you add per day, per month and per
        game — the part of the story Steam does not record.
      </p>
      <div class="seo-stats">
        <div class="seo-stat">
          <div class="label">Source</div>
          <div class="value">Steam's own hours</div>
        </div>
        <div class="seo-stat">
          <div class="label">Setup</div>
          <div class="value">One Steam sign-in</div>
        </div>
        <div class="seo-stat">
          <div class="label">Cost</div>
          <div class="value">Free</div>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>Steam Total Hours FAQ</h2>
      <div class="seo-faq">
        <div class="seo-faq-item">
          <h3>Does Steam show your total playtime?</h3>
          <p>No. The library, your profile's Games page and your reviews all show hours per game. Steam has no account-wide total, so you either add every game up yourself or use a tool that does.</p>
        </div>
        <div class="seo-faq-item">
          <h3>Is the total exact?</h3>
          <p>It is exactly what Steam reports: the sum of every game's hours on record. Steam's own per-game figures are what go in, so the total matches your profile.</p>
        </div>
        <div class="seo-faq-item">
          <h3>Can I see when I played those hours?</h3>
          <p>Only from the day tracking starts. Steam keeps lifetime totals, not a history, so hours played before you added your profile cannot be split by day. Everything after that is recorded daily.</p>
        </div>
        <div class="seo-faq-item">
          <h3>Why does my total show zero?</h3>
          <p>Your playtime is probably hidden. In Steam's privacy settings set Game details to Public and untick the option to always keep your total playtime private.</p>
        </div>
      </div>
    </section>
    <SeoRelatedTools current-slug="steam-total-hours" />
  </div>
</template>

<script>
import { applyMeta, pageStructuredData } from '../utils/meta';
import SeoRelatedTools from '../components/SeoRelatedTools.vue';

const META_DESCRIPTION = 'Steam never adds up your hours. See your total Steam playtime across every game on your account, with your most-played games ranked — free, one Steam sign-in.';

const META = {
  title: 'Steam Total Hours: Your Total Playtime Across Every Game | steamplayti.me',
  description: META_DESCRIPTION,
  keywords: 'steam total hours, steam total playtime, total steam hours, steam account hours, steam total hours played',
  canonical: 'https://steamplayti.me/page/steam-total-hours/',
  ogTitle: 'See Your Total Steam Hours',
  ogDescription: 'Your whole Steam library summed into one number, with your most-played games ranked.',
  ogUrl: 'https://steamplayti.me/page/steam-total-hours/',
  twitterTitle: 'See Your Total Steam Hours',
  twitterDescription: 'Steam shows hours per game but never the total. steamplayti.me adds it up.',
  structuredData: pageStructuredData({
    name: 'Steam Total Hours',
    description: META_DESCRIPTION,
    canonical: 'https://steamplayti.me/page/steam-total-hours/',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Tools & Blog', path: '/blog/' },
      { name: 'Steam Total Hours', path: 'https://steamplayti.me/page/steam-total-hours/' },
    ],
  }),
};

export default {
  name: 'SteamTotalHoursPage',
  components: { SeoRelatedTools },
  mounted() {
    applyMeta(META);
  },
  beforeUnmount() {
    applyMeta();
  },
};
</script>

<style src="../styles/seo-page.css"></style>
